import { useMemo } from 'react'

import useQuery from '@api/hooks/useQuery'
import { BasketStats } from '@domain'
import { faPiggyBank } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Stack,
	Typography,
	useTheme,
} from '@mui/material'
import { LineChart } from '@mui/x-charts-pro'
import MarketBasketReportsApi, { MarketBasketReportsKeys } from '@services/marketBasketReports.api'
import { numToPercentage } from '@utils/stringHelpers'
import dayjs from 'dayjs'
import { StringParam, useQueryParam, withDefault } from 'use-query-params'

import { buildDateRange, dateRanges, toAverageSavingsPcts } from './Charts.utils'

export default function SavingsPercentage() {
	const [range, setRange] = useQueryParam(
		'savings-percentage-range',
		withDefault(StringParam, '7-days')
	)

	const { dateRangeStart, dateRangeEnd } = useMemo(() => buildDateRange(range), [range])

	const { data, isLoading } = useQuery({
		queryKey: MarketBasketReportsKeys.basketStats({ dateRangeStart, dateRangeEnd }),
		queryFn: () => MarketBasketReportsApi.basketStats({ dateRangeStart, dateRangeEnd }),
	})

	return (
		<SavingsPercentageUI
			data={data || []}
			isLoading={isLoading}
			range={range}
			setRange={setRange}
		/>
	)
}

type SavingsPercentageUIProps = {
	data: BasketStats[]
	isLoading?: boolean
	range: string
	setRange: (range: string) => void
}

export function SavingsPercentageUI({
	data,
	isLoading = false,
	range,
	setRange,
}: SavingsPercentageUIProps) {
	const isDaily = range === '7-days' || range === '30-days'

	const dataset = useMemo(() => {
		if (!data || data.length === 0) return []
		return toAverageSavingsPcts(data, isDaily ? 'day' : 'week')
	}, [data, isDaily])

	return (
		<Paper elevation={1} sx={{ width: '100%' }}>
			<Stack p={2} width="100%" direction="row" justifyContent="space-between" alignItems="center">
				<Stack direction="row" spacing={2} p="1rem" alignItems="center">
					<FontAwesomeIcon icon={faPiggyBank} size="lg" />
					<Typography variant="h6">Savings % Per {isDaily ? 'Day' : 'Week'}</Typography>
					<Typography variant="body2" color="text.secondary">
						in basket
					</Typography>
				</Stack>
				<FormControl sx={{ width: '13.75rem' }}>
					<InputLabel>Range</InputLabel>
					<Select
						data-testid="range-select"
						value={range}
						label="Range"
						onChange={(val) => setRange(val.target.value)}
					>
						{dateRanges.map((range) => (
							<MenuItem key={range.value} value={range.value}>
								{range.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Stack>

			<Chart isLoading={isLoading} data={dataset} isDaily={isDaily} />
		</Paper>
	)
}

type ChartProps = {
	isLoading: boolean
	data: { date: Date; totalPct: number; pricePct: number; rebatePct: number }[]
	isDaily: boolean
}

function Chart({ isLoading, data, isDaily }: ChartProps) {
	const theme = useTheme()

	if (isLoading) return <Skeleton variant="rectangular" width="100%" height={500} />

	if (data.length === 0)
		return (
			<Stack width="100%" height="500px" justifyContent="center" alignItems="center">
				<Typography variant="body2">No data available for this period.</Typography>
			</Stack>
		)

	return (
		<LineChart
			dataset={data}
			xAxis={[
				{
					dataKey: 'date',
					scaleType: 'time',
					valueFormatter: (v) => dayjs(v as Date).format('MMM D'),
					tickMinStep: isDaily
						? 1000 * 60 * 60 * 24 // 1 day
						: 1000 * 60 * 60 * 24 * 7, // 1 week
				},
			]}
			yAxis={[
				{
					scaleType: 'linear',
					valueFormatter: (v) => numToPercentage(v),
				},
			]}
			series={[
				{
					id: 'totalPct',
					dataKey: 'totalPct',
					label: 'Total Savings %',
					color: theme.palette.success.main,
					valueFormatter: (v) => numToPercentage(v),
				},
				{
					id: 'pricePct',
					dataKey: 'pricePct',
					label: 'Price Savings %',
					color: theme.palette.primary.main,
					valueFormatter: (v) => numToPercentage(v),
				},
				{
					id: 'rebatePct',
					dataKey: 'rebatePct',
					label: 'Rebate Savings %',
					color: theme.palette.secondary.main,
					valueFormatter: (v) => numToPercentage(v),
				},
			]}
			height={500}
			grid={{ horizontal: true }}
			slotProps={{
				legend: { position: { vertical: 'bottom' }, sx: { mb: 2 } },
			}}
		/>
	)
}
